import promiseRetry from 'promise-retry';
import { matchPath } from 'react-router-dom';
import { getCountryCodeFromLocale } from '@cbs-mp/brands-config';
import { version, name } from '../../../package.json';
import { selectors } from '../redux/authentication';
import {
    areSubscriptionsPending,
    getSingleSubscriptionPending,
    getSubscriptions,
    getSingleSubscription,
} from '../redux/subscription/selectors';

export const TRACKING_READY = 'TRACKING_READY';
export const TRACKING_READY_CHECK_RETRY_PARAMS = {
    retries: 8,
    factor: 2,
    minTimeout: 50,
    maxTimeout: 2000,
};

export const SUBSCRIPTION_FETCH_SCOPE = {
    ALL: 'ALL',
    SINGLE: 'SINGLE',
};

const getRegionFromProvider = () => {
    const countryCode = getCountryCodeFromLocale(CONFIG.myPage.publication);
    const regionMap = {
        NO: 'Norway',
        SE: 'Sweden',
    };

    return regionMap[countryCode];
};

const hasSubscriptionData = (data) => {
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0 && data.some(sub => sub?.name);
    if (typeof data === 'object') return !!data.name;
    return false;
};

const isOnSubscriptionRequiredPath = () => {
    const { pathname } = window.location;
    const { basePath } = CONFIG;

    const sharingPaths = [
        ...(CONFIG.subPagesPaths.sharing?.manage ? [`${basePath}${CONFIG.subPagesPaths.sharing.manage}`] : []),
        ...(CONFIG.subPagesPaths.sharing?.login ? [`${basePath}${CONFIG.subPagesPaths.sharing.login}`] : []),
        ...(CONFIG.subPagesPaths.sharing?.accept ? [`${basePath}${CONFIG.subPagesPaths.sharing.accept}`] : []),
    ];

    const deliveryBasePath = `${basePath}${CONFIG.subPagesPaths.manageDeliveries.base}`;

    const matchesPattern = pattern =>
        matchPath({ path: pattern, end: false }, pathname) !== null;

    return sharingPaths.some(matchesPattern) || matchesPattern(deliveryBasePath);
};

export const formatUser = (state) => {
    const userId = state?.spid?.userId;

    if (!userId) {
        return null;
    }

    const subscriptions = getSubscriptions(state);
    const singleSubscription = getSingleSubscription(state);

    let hasAnySubscription = hasSubscriptionData(subscriptions) || hasSubscriptionData(singleSubscription);

    if (!hasAnySubscription && isOnSubscriptionRequiredPath()) {
        hasAnySubscription = true;
    }
    return {
        userId: String(userId),
        userSdrn: `sdrn:${CONFIG.tracking.spidRealm}:user:${userId}`,
        hasAnySubscription,
        region: getRegionFromProvider(),
    };
};

export const provider = {
    providerId: CONFIG.tracking.provider,
    providerSdrn: `sdrn:schibsted:client:${CONFIG.tracking.provider}`,
    appProduct: name,
    appVersion: version,
};

export const formatBaseParams = pageType => ({
    provider,
    object: {
        category: 'MyPage',
        objectId: pageType,
        title: pageType,
        objectType: 'Page',
        objectSdrn: `sdrn:${CONFIG.tracking.provider}:page:${pageType}`,
    },
});

export const formatPageViewEventParams = (pageType, customProperties = {}) => ({
    ...formatBaseParams(pageType),
    object: {
        ...formatBaseParams(pageType).object,
        ...customProperties,
    },
});

const createUIElementObject = (id, elementType, customProperties = {}) => {
    const { objectId: customObjectId, ...validProperties } = customProperties;
    const finalObjectId = customObjectId || id;
    return {
        objectId: finalObjectId,
        name: id,
        objectType: 'UIElement',
        objectSdrn: `sdrn:${CONFIG.tracking.provider}:uielement:${finalObjectId}`,
        elementType,
        ...validProperties,
    };
};

/** // TODO: Remove when fully migrate to new tracking (TPAAS)
 * @param {object} params
 * @param {string} params.id
 * @param {string} [params.url] - Only used for legacy tracking, not passed to TPAAS
 * @param {string} [params.elementType]
 * @param {string} [params.contentId] - Only used for legacy tracking, not passed to TPAAS
 * @param {string} [params.objectId]
 */
export const formatUIEventParams = ({
    id,
    url, // Accepted for API compatibility but not passed to TPAAS
    elementType = 'Button',
    contentId, // Accepted for API compatibility but not passed to TPAAS
    objectId,
    ...customProperties
}) => ({
    provider,
    object: createUIElementObject(id, elementType, {
        ...(objectId !== undefined && { objectId }),
        ...customProperties,
    }),
    placement: {
        containerName: 'page',
    },
    engagement: {
        action: 'Click',
        elementName: id,
    },
});

export const formatUIImpressionEventParams = ({
    id,
    elementType = 'Modal',
    foldPosition = 'Above',
    ...customProperties
}) => ({
    provider,
    object: createUIElementObject(id, elementType, customProperties),
    placement: {
        containerName: 'page',
        foldPosition,
    },
});

export const formatFormEventParams = ({ type, id, ...customProperties }) => ({
    provider,
    object: createUIElementObject(id, 'Form', customProperties),
    placement: {
        containerName: 'page',
    },
    engagement: {
        action: type,
        elementName: id,
    },
});

const subscriptionListFetchPaths = [CONFIG.basePath];
const singleSubscriptionFetchPaths = [`${CONFIG.basePath}${CONFIG.subPagesPaths.subscriptionSingle}`];

export const getSubscriptionFetchScope = () => {
    const { pathname } = window.location;

    const matchesPath = pattern =>
        matchPath({ path: pattern, end: true }, pathname) !== null;

    if (subscriptionListFetchPaths.some(matchesPath)) return SUBSCRIPTION_FETCH_SCOPE.ALL;
    if (singleSubscriptionFetchPaths.some(matchesPath)) return SUBSCRIPTION_FETCH_SCOPE.SINGLE;

    return null;
};

const isTrackingReady = getState => retry => new Promise((resolve, reject) => {
    const state = getState();
    const isAuthenticated = selectors.isUserAuthenticated(state);
    const userId = state?.spid?.userId;
    const scope = getSubscriptionFetchScope();

    if (!isAuthenticated || !userId) {
        reject();
        return;
    }

    if (scope === SUBSCRIPTION_FETCH_SCOPE.ALL && areSubscriptionsPending(state)) {
        reject();
        return;
    }

    if (scope === SUBSCRIPTION_FETCH_SCOPE.SINGLE && getSingleSubscriptionPending(state)) {
        reject();
        return;
    }

    if (scope === null) {
        resolve(TRACKING_READY);
        return;
    }

    const subscriptions = getSubscriptions(state);
    const singleSubscription = getSingleSubscription(state);

    if (scope === SUBSCRIPTION_FETCH_SCOPE.ALL) {
        if (subscriptions !== undefined) {
            resolve(TRACKING_READY);
            return;
        }
        reject();
        return;
    }

    if (scope === SUBSCRIPTION_FETCH_SCOPE.SINGLE) {
        if (singleSubscription !== undefined) {
            resolve(TRACKING_READY);
            return;
        }
        reject();
        return;
    }

    reject();
}).catch(retry);

export const waitUntilTrackingReady = getState => promiseRetry(
    isTrackingReady(getState),
    TRACKING_READY_CHECK_RETRY_PARAMS
);

export function applyExperiments(state) {
    const experiments = state?.login?.experiments || {};

    window?.Tracker?.update({
        experiments: Object.entries(experiments).map(([experimentName, variant]) => ({
            id: experimentName,
            variant: variant || 'default',
            platform: CONFIG.tracking.provider,
        })),
    });
}
